"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { BlogCard } from "@/components/blog/BlogCard";
import type { BlogPostListEntry } from "@/content/blog/types";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

interface LatestInsightsSectionProps {
  readonly posts: readonly BlogPostListEntry[];
  readonly locale?: Locale;
}

export default function LatestInsightsSection({
  posts,
  locale = defaultLocale,
}: LatestInsightsSectionProps) {
  if (!posts || posts.length === 0) {
    return null;
  }

  const [featured, ...rest] = posts;
  const secondary = rest.slice(0, 2);
  const featuredHref = getLocalizedPathname(`/blog/${featured.slug}/`, locale);
  const blogHref = getLocalizedPathname("/blog/", locale);

  return (
    <section className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Insights")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Latest on")}{" "}
              <span className="emphasis-underline">
                {t(locale, "AI & Agentic")}
              </span>
            </h2>
          </div>
          <Link
            href={blogHref}
            className="group inline-flex shrink-0 items-center gap-2 font-semibold text-primary transition-colors hover:text-primary/80"
          >
            {t(locale, "View all articles")}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Featured latest AI article */}
        <Link
          href={featuredHref}
          prefetch={false}
          className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow duration-200 hover:shadow-lg"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {featured.heroImage && (
              <div className="h-56 overflow-hidden lg:h-full lg:min-h-[340px]">
                <img
                  src={featured.heroImage}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                  width={800}
                  height={600}
                  decoding="async"
                />
              </div>
            )}
            <div className="flex flex-col justify-center p-8 lg:p-12">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                  {t(locale, "Latest")}
                </span>
                {featured.labels.slice(0, 2).map((label) => (
                  <span
                    key={label}
                    className="rounded-full border border-neutral-border bg-neutral-surface px-2.5 py-0.5 text-xs font-medium text-neutral-emphasis"
                  >
                    {label}
                  </span>
                ))}
              </div>
              <h3 className="font-display text-2xl font-bold leading-tight tracking-tight text-neutral-emphasis transition-colors duration-200 group-hover:text-primary md:text-3xl">
                {featured.title}
              </h3>
              <p className="mt-4 leading-relaxed text-neutral-muted line-clamp-3">
                {featured.description}
              </p>
              <div className="mt-5 flex items-center gap-2 text-sm text-neutral-muted">
                <time dateTime={featured.date}>{featured.date}</time>
                <span>•</span>
                <span>{featured.readingTime}</span>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 font-semibold text-primary">
                {t(locale, "Read article")}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </div>
          </div>
        </Link>

        {secondary.length > 0 && (
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
            {secondary.map((post) => (
              <BlogCard
                key={post.slug}
                slug={post.slug}
                title={post.title}
                description={post.description}
                date={post.date}
                readingTime={post.readingTime}
                labels={post.labels}
                heroImage={post.heroImage}
                thumbnailImage={post.thumbnailImage}
                locale={locale}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
